import type {

  TasklistPageKeepLearningEntry,

  TasklistPageSection,

  TasklistPageSubtask,

  TasklistSliceDisplayConditions,

} from '@/types/tasklistPageJson';

import {

  deriveOlsAvailability,

  isK12SchoolCtaVisible,

  resolveK12SchoolOpenHref,

  showCanvasInlineLinks,

  type SchoolLinkSnapshot,

  EMPTY_SCHOOL_LINK_SNAPSHOT,

} from '@/utils/schoolLinkKind';



/**

 * Map selected student's grade to CMS JSON `displayConditions.gradeBand` values.

 */

export function studentGradeToJsonBand(gradeId: string | undefined): 'k-5' | '6-8' | '9-12' | null {

  if (!gradeId?.trim()) return null;

  const g = gradeId.trim().toUpperCase();

  const elementary = ['K', '0', '1', '2', '3', '4', '5'];

  if (elementary.includes(g)) return 'k-5';

  const n = Number(g);

  if (!Number.isNaN(n)) {

    if (n >= 6 && n <= 8) return '6-8';

    if (n >= 9 && n <= 12) return '9-12';

  }

  return null;

}



/** Runtime flags used with CMS `displayConditions.olsAvailable` / `launchpadAvailable`. */

export interface TasklistDisplayRuntime {

  olsAvailable: boolean;

  launchpadAvailable: boolean;

}



/**

 * Derives OLS and Launch Pad flags for CMS slice matching.

 * When `tool200Active` is provided, it reflects LaunchPad tool ID 200 (status === 1).

 * Otherwise Launch Pad falls back to K–5 grade band for loading-safe behavior.

 *

 * `olsAvailable` follows API `courseAssigned` (not URL hostname inference).

 */

export function getTasklistDisplayRuntime(

  schoolLink: SchoolLinkSnapshot | null | undefined,

  gradeId: string | undefined,

  tool200Active?: boolean

): TasklistDisplayRuntime {

  const snapshot = schoolLink ?? EMPTY_SCHOOL_LINK_SNAPSHOT;

  const launchpadAvailable =

    typeof tool200Active === 'boolean'

      ? tool200Active

      : studentGradeToJsonBand(gradeId) === 'k-5';



  return {

    olsAvailable: deriveOlsAvailability(snapshot.courseAssigned),

    launchpadAvailable,

  };

}



/** LP/OLS runtime plus resolved primary K12 School CTA (Hero, Footer, school info). */

export interface K12SchoolPortalUiSnapshot {

  runtime: TasklistDisplayRuntime;

  /** When false, hide the primary K12 School CTA (matrix: LP off and OLS off). */

  showK12SchoolCta: boolean;

  /** URL to open when the CTA is enabled; null when there is no safe target yet. */

  k12SchoolHref: string | null;

  /** Calendar / canvas-link inline URLs (LP off + OLS on). */

  showCanvasInlineLinks: boolean;

}



export function getK12SchoolPortalResolution(

  schoolLink: SchoolLinkSnapshot | null | undefined,

  gradeId: string | undefined,

  tool200Active?: boolean

): K12SchoolPortalUiSnapshot {

  const snapshot = schoolLink ?? EMPTY_SCHOOL_LINK_SNAPSHOT;

  const runtime = getTasklistDisplayRuntime(snapshot, gradeId, tool200Active);

  const { launchpadAvailable, olsAvailable } = runtime;

  return {

    runtime,

    showK12SchoolCta: isK12SchoolCtaVisible(launchpadAvailable, olsAvailable),

    k12SchoolHref: resolveK12SchoolOpenHref(

      launchpadAvailable,

      olsAvailable,

      snapshot.schoolLinkUrl,

      snapshot.isCanvasPortal

    ),

    showCanvasInlineLinks: showCanvasInlineLinks(

      launchpadAvailable,

      olsAvailable,

      snapshot.schoolLinkUrl

    ),

  };

}



function roleMatches(conditions: TasklistSliceDisplayConditions | undefined, userRoleVal: string | undefined): boolean {

  if (!conditions?.userRole?.length) return true;

  const role = userRoleVal ?? '';

  const map: Record<string, string> = {

    learningCoach: 'LEARNING_COACH',

    student: 'STUDENT',

  };

  return conditions.userRole.some((r) => {

    const expected = map[r] ?? r.replace(/-/g, '_').toUpperCase();

    return expected === role || r.toUpperCase() === role;

  });

}



function gradeBandMatches(conditions: TasklistSliceDisplayConditions | undefined, gradeId: string | undefined): boolean {

  if (!conditions?.gradeBand?.length) return true;

  const band = studentGradeToJsonBand(gradeId);

  if (!band) return false;

  const want = conditions.gradeBand.map((b) => b.trim().toLowerCase());

  return want.includes(band);

}

function ageRangeMatches(
  conditions: TasklistSliceDisplayConditions | undefined,
  studentAge: number | undefined | null
): boolean {
  const range = conditions?.ageRange;
  if (!range) return true;

  const min = range.min;
  const max = range.max;
  const hasMin = typeof min === 'number' && !Number.isNaN(min);
  const hasMax = typeof max === 'number' && !Number.isNaN(max);
  if (!hasMin && !hasMax) return true;

  if (typeof studentAge !== 'number' || Number.isNaN(studentAge)) return false;
  if (hasMin && studentAge < min) return false;
  /** `max: 13` means under 13 — matches legacy School Documents (`age < 13`). */
  if (hasMax && studentAge >= max) return false;
  return true;
}

function olsLaunchPadMatches(conditions: TasklistSliceDisplayConditions | undefined, runtime: TasklistDisplayRuntime): boolean {

  const ols = conditions?.olsAvailable;

  if (typeof ols === 'boolean' && ols !== runtime.olsAvailable) return false;

  const lp = conditions?.launchpadAvailable;

  if (typeof lp === 'boolean' && lp !== runtime.launchpadAvailable) return false;

  return true;

}



/**

 * CMS slice visibility: empty userRole / gradeBand arrays mean "no filter" for that dimension.

 * When `runtime` is passed, `olsAvailable` / `launchpadAvailable` in JSON (boolean only) must match runtime; omit or `null` = no filter on that axis.

 * When `ageRange` is set, `studentAge` from the user API must fall within bounds (`max` is exclusive).

 */

export function shouldShowTasklistSlice(

  conditions: TasklistSliceDisplayConditions | undefined,

  userRoleVal: string | undefined,

  gradeId: string | undefined,

  runtime?: TasklistDisplayRuntime,

  studentAge?: number | null

): boolean {

  if (!roleMatches(conditions, userRoleVal) || !gradeBandMatches(conditions, gradeId)) return false;

  if (!ageRangeMatches(conditions, studentAge)) return false;

  if (runtime && !olsLaunchPadMatches(conditions, runtime)) return false;

  return true;

}



function isKeepLearningEntry(x: unknown): x is TasklistPageKeepLearningEntry {

  if (!x || typeof x !== 'object') return false;

  const o = x as Record<string, unknown>;

  return typeof o.markdown === 'string' && o.markdown.trim().length > 0;

}



/** CMS `content[]` sections filtered by role, grade band, OLS, and Launch Pad runtime. */

export function getVisibleContentSections(

  content: TasklistPageSection[] | undefined,

  userRoleVal: string | undefined,

  gradeId: string | undefined,

  runtime?: TasklistDisplayRuntime,

  studentAge?: number | null

): TasklistPageSection[] {

  if (!content?.length) return [];

  return content.filter((section) =>

    shouldShowTasklistSlice(section.displayConditions, userRoleVal, gradeId, runtime, studentAge)

  );

}



/** CMS `subtasks[]` filtered by `displayConditions`. */

export function getVisibleSubtasks(

  subtasks: TasklistPageSubtask[] | undefined,

  userRoleVal: string | undefined,

  gradeId: string | undefined,

  runtime?: TasklistDisplayRuntime,

  studentAge?: number | null

): TasklistPageSubtask[] {

  if (!subtasks?.length) return [];

  return subtasks.filter((st) =>

    shouldShowTasklistSlice(st.displayConditions, userRoleVal, gradeId, runtime, studentAge)

  );

}



/**

 * CMS `keepLearning[]` entries: each slice may include `displayConditions` (role, grade band, OLS, Launch Pad).

 */

export function getVisibleKeepLearningEntries(

  keepLearning: unknown,

  userRoleVal: string | undefined,

  gradeId: string | undefined,

  runtime?: TasklistDisplayRuntime,

  studentAge?: number | null

): TasklistPageKeepLearningEntry[] {

  if (!Array.isArray(keepLearning)) return [];

  return keepLearning

    .filter(isKeepLearningEntry)

    .filter((item) => shouldShowTasklistSlice(item.displayConditions, userRoleVal, gradeId, runtime, studentAge));

}


